import React, { useEffect, useState } from "react";
import { setGlobalLoading } from "../context/LoaderController";
import { fetchMemberCount, fetchDistrictAssemblyCount } from "../services/api";
import {
  LOCALSTORAGE_SELECTED_STATE_CODE,
  LOCALSTORAGE_SELECTED_STATE_NAME,
  PAGE_TITLE_MEMBERSHIP_COUNT,
  NO_STATE_SELECTED_MESSAGE,
  LOADING_MEMBERSHIP_COUNT,
  ERROR_FETCH_MEMBER_COUNT,
  STATE_LABEL,
  MEMBER_COUNT_TOTAL_LABEL,
  MEMBER_COUNT_PAID_LABEL,
  MEMBER_COUNT_UNPAID_LABEL,
} from "../components/constants";
import "../App.css";

export default function MembershipCount() {
  const storedStateId = localStorage.getItem(LOCALSTORAGE_SELECTED_STATE_CODE);
  const storedStateName =
    localStorage.getItem(LOCALSTORAGE_SELECTED_STATE_NAME) || "";

  const [stateId] = useState(storedStateId ?? "");

  const [memberCountTotal, setMemberCountTotal] = useState(0);
  const [memberCountPaid, setMemberCountPaid] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [districtAssemblies, setDistrictAssemblies] = useState([]);
  const [showDA, setShowDA] = useState(false);
  const [daLoading, setDaLoading] = useState(false);

  /* ---------- NEW: District Assembly modal state ---------- */
  const [showDAModal, setShowDAModal] = useState(false);

  const memberCountUnpaid = Math.max(memberCountTotal - memberCountPaid, 0);

  /* ================= LOAD MEMBER COUNTS FIRST ================= */
  useEffect(() => {
    if (!stateId) return;

    const loadCounts = async () => {
      setGlobalLoading(true);
      setError("");

      try {
        const data = await fetchMemberCount(stateId);
        setMemberCountTotal(Number(data.memberCountTotal || 0));
        setMemberCountPaid(Number(data.memberCount || 0));
      } catch (err) {
        console.error(err);
        setError(ERROR_FETCH_MEMBER_COUNT);
      } finally {
        setGlobalLoading(false);
      }
    };

    loadCounts();
  }, [stateId]);

  /* ================= OPEN DISTRICT ASSEMBLY MODAL ================= */
  const handleToggleDistrictAssemblies = async () => {
    if (!stateId) return;

    // Open modal immediately
    setShowDAModal(true);

    // If already fetched, just show modal
    if (districtAssemblies.length > 0) return;

    // First-time fetch
    setDaLoading(true);

    try {
      const da = await fetchDistrictAssemblyCount(stateId);
      setDistrictAssemblies(Array.isArray(da) ? da : []);
    } catch (err) {
      console.error(err);
      setDistrictAssemblies([]);
    } finally {
      setDaLoading(false);
    }
  };

  /* ================= NO STATE SELECTED ================= */
  if (!stateId) {
    return (
      <div className="main-container">
        <h2 className="section-title">{PAGE_TITLE_MEMBERSHIP_COUNT}</h2>
        <p>
          {NO_STATE_SELECTED_MESSAGE.split("Home")[0]}
          <a href="/" className="link">Home</a> page first.
        </p>
      </div>
    );
  }

  return (
    <div className="main-container">
      <h2 className="section-title">{PAGE_TITLE_MEMBERSHIP_COUNT}</h2>

      <span className="iyc-badge">
        {STATE_LABEL} <strong>{storedStateName || stateId}</strong>
      </span>

      {/* Membership refresh info */}
      <div
        style={{
          marginTop: "16px",
          padding: "12px 16px",
          backgroundColor: "#FDECEC",
          border: "1px solid #F5C2C7",
          borderRadius: "8px",
          color: "#B02A37",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontSize: "14px",
          fontWeight: 600,
        }}
      >
        <span style={{ fontSize: "18px" }}>🔴</span>
        <span>
          Membership counts are refreshed every <strong>hour</strong>.
        </span>
      </div>

      {loading && <p>{LOADING_MEMBERSHIP_COUNT}</p>}
      {error && <p style={{ color: "red" }}>❌ {error}</p>}

      {/* ================= MEMBER COUNT SUMMARY ================= */}
      {!loading && !error && (
        <div className="votes-summary-container">
          <div className="votes-summary-box did-not-vote">
            <div className="votes-summary-label">
              {MEMBER_COUNT_TOTAL_LABEL}
            </div>
            <div className="votes-summary-value">{memberCountTotal}</div>
          </div>

          <div className="votes-summary-box">
            <div className="votes-summary-label">
              {MEMBER_COUNT_PAID_LABEL}
            </div>
            <div className="votes-summary-value">{memberCountPaid}</div>
          </div>

          <div className="votes-summary-box did-not-vote">
            <div className="votes-summary-label">
              {MEMBER_COUNT_UNPAID_LABEL}
            </div>
            <div className="votes-summary-value">{memberCountUnpaid}</div>
          </div>
        </div>
      )}

      {/* ================= BUTTON ================= */}
      <div style={{ marginTop: "24px" }}>
        <button
          className="iyc-primary-btn"
          onClick={handleToggleDistrictAssemblies}
          disabled={daLoading}
        >
          {daLoading ? "Loading…" : "Show District Assembly Member Count"}
        </button>
      </div>

      {/* ================= DISTRICT ASSEMBLY MODAL ================= */}
      {showDAModal && (
        <div
          className="iyc-modal-overlay"
          onClick={() => setShowDAModal(false)}
        >
          <div
            className="iyc-modal-card iyc-referrer-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="iyc-modal-header">
              <div>
                <h3 className="iyc-modal-title">
                  District Assembly Member Count
                </h3>
                <p className="iyc-modal-subtitle">
                  {STATE_LABEL} <strong>{storedStateName || stateId}</strong>
                </p>
              </div>

              <button
                className="iyc-modal-close"
                type="button"
                onClick={() => setShowDAModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="iyc-modal-body">
              {daLoading && <p>Loading district assembly counts…</p>}

              {!daLoading && districtAssemblies.length === 0 && (
                <p>No district assembly member count available for this state.</p>
              )}

              {!daLoading && districtAssemblies.length > 0 && (
                <div className="table-container">
                  <table className="common-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>District</th>
                        <th>Assembly</th>
                        <th>Total Count</th>
                        <th>Paid Count</th>
                      </tr>
                    </thead>

                    <tbody>
                      {districtAssemblies.map((row, i) => (
                        <tr key={`${row.assemblyCode}-${i}`}>
                          <td>{i + 1}</td>
                          <td>{row.districtName}</td>
                          <td>{row.assemblyName}</td>
                          <td>{row.totalCount ?? 0}</td>
                          <td>{row.paidCount ?? 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}